import { useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { PROJECTION_EXTRA_HOURS } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.constants';
import { BoosterProjection } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

import { Box, Slider, SxProps, Theme, Typography } from '@mui/material';

const STRINGS = appStrings.modules.satActBooster.results.projection;

type ProjectionCardProps = Readonly<{
  /** The recompute-on-read projection; the slider indexes into its what-if curve. */
  projection: BoosterProjection;
}>;

/**
 * "Projected Test-Day Score": a projected composite shown next to its estimated ± range (never a
 * bare number — the range is the honesty mechanism), and a "what if I added X hrs/week" slider that
 * indexes the projection's precomputed curve (no network on drag).
 */
export const ProjectionCard = ({ projection }: ProjectionCardProps) => {
  const [extraHours, setExtraHours] = useState<number>(PROJECTION_EXTRA_HOURS.default);

  // Select the curve point by its extraHours (not array position, so a shorter or
  // differently-stepped curve can't desync the shown value from the label). At rest the
  // headline is the projection's own composite/±, not the curve's first entry.
  const selected =
    extraHours > PROJECTION_EXTRA_HOURS.min
      ? projection.curve.find((point) => point.extraHours === extraHours)
      : undefined;
  const composite = selected?.composite ?? projection.composite;
  const ci95 = selected?.ci95 ?? projection.ci95;
  const weeks = Math.round(projection.weeksRemaining);

  // Bound the slider by the curve itself, so it can't offer an extra-hours value the curve
  // doesn't carry (a miss would silently fall back to the base composite).
  const sliderMax = Math.max(...projection.curve.map((point) => point.extraHours));

  return (
    <Box sx={cardSx} id="booster-projection-card">
      <Box sx={headingSx}>
        <Typography variant="text-sm-semibold" sx={headingTextSx}>
          {STRINGS.label}
        </Typography>
      </Box>

      <Box sx={contentSx}>
        <Box sx={scoreRowSx}>
          <Typography variant="display-sm-semibold" sx={scoreSx}>
            {composite}
          </Typography>
          <Box sx={rangeSx} aria-label={STRINGS.rangeAria(ci95)}>
            <Typography variant="text-sm-medium" sx={rangeValueSx}>
              {STRINGS.band(ci95)}
            </Typography>
          </Box>
        </Box>

        <Typography variant="text-sm-regular" sx={summarySx}>
          {projection.current != null
            ? STRINGS.summary(weeks, projection.current)
            : STRINGS.summaryNoScore(weeks)}
        </Typography>

        <Box sx={sliderGroupSx}>
          <Typography
            component="label"
            htmlFor="booster-projection-whatif-slider"
            variant="text-sm-semibold"
            sx={sliderLabelSx}
          >
            {STRINGS.whatIf(extraHours)}
          </Typography>
          <Slider
            id="booster-projection-whatif-slider"
            aria-label={STRINGS.whatIfAria}
            value={extraHours}
            min={PROJECTION_EXTRA_HOURS.min}
            max={sliderMax}
            step={PROJECTION_EXTRA_HOURS.step}
            onChange={(_event, value) => setExtraHours(value as number)}
            sx={sliderSx}
          />
        </Box>
      </Box>
    </Box>
  );
};

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  backgroundColor: backrs.colors['Colors/Background/bg-secondary-subtle'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
});

const headingSx: SxProps<Theme> = ({ backrs }) => ({
  paddingTop: backrs.spacing['spacing-lg'],
  paddingBottom: backrs.spacing['spacing-md'],
  paddingLeft: backrs.spacing['spacing-2xl'],
  paddingRight: backrs.spacing['spacing-2xl'],
});

const headingTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const contentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  gap: backrs.spacing['spacing-2xl'],
  padding: backrs.spacing['spacing-2xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
  boxShadow: backrs.shadows['shadow-xs'],
});

const scoreRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
});

const scoreSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const rangeSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xxs'],
});

const rangeValueSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-brand-tertiary'],
});

const summarySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const sliderGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const sliderLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const sliderSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Foreground/fg-brand-primary'],
  height: '2px',
  padding: `${backrs.spacing['spacing-lg']} 0`,
  '& .MuiSlider-rail': {
    backgroundColor: backrs.colors['Colors/Foreground/fg-brand-primary'],
    opacity: 1,
    height: '2px',
  },
  '& .MuiSlider-track': {
    backgroundColor: backrs.colors['Colors/Foreground/fg-brand-primary'],
    border: 'none',
    height: '2px',
  },
  '& .MuiSlider-thumb': {
    width: '24px',
    height: '24px',
    backgroundColor: backrs.colors['Colors/Background/bg-brand-primary'],
    border: `2px solid ${backrs.colors['Colors/Foreground/fg-brand-primary']}`,
    '&:hover, &.Mui-focusVisible, &.Mui-active': {
      boxShadow: 'none',
    },
    '&::before': {
      boxShadow: 'none',
    },
  },
});
